import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, CalendarCheck, Envelope, WarningCircle } from "@phosphor-icons/react";
import { GettingHere } from "../components/GettingHere";
import SEO from "../components/SEO";
import { trackEvent } from "../components/Analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 20; // ~40s

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
    });
  } catch { return iso; }
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [booking, setBooking] = useState(null);
  const [inviteeEmail, setInviteeEmail] = useState("");
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const resp = await fetch(`${API}/api/payments/status/${sessionId}`);
        if (!resp.ok) throw new Error(`status ${resp.status}`);
        const data = await resp.json();
        if (cancelled) return;
        if (data.booking_failed) {
          setStatus("slot_lost");
          return;
        }
        if (data.needs_manual_booking) {
          setInviteeEmail(data.invitee_email || "");
          setStatus("manual");
          return;
        }
        if (data.payment_status === "paid") {
          setStatus("paid");
          setInviteeEmail(data.invitee_email || "");
          // booking snapshot is populated asynchronously by the webhook — keep polling briefly for it
          if (data.booking) {
            setBooking(data.booking);
          } else if (attemptsRef.current < MAX_ATTEMPTS) {
            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
          trackEvent("payment_succeeded", { session_id: sessionId });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
        } else {
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const whenStr = booking ? formatWhen(booking.pre_slots?.[0]?.start_time || booking.start_time) : "";

  return (
    <div data-testid="payment-success-page">
      <SEO title="Booking confirmed" path="/payment/success" />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-16 max-w-5xl">
          {status === "polling" && (
            <div data-testid="payment-status-polling">
              <div className="eyebrow">Confirming payment</div>
              <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
                Just a moment - we&apos;re confirming your payment with Stripe.
              </h1>
              <div className="mt-8 flex items-center gap-3 text-ink-muted">
                <span className="inline-block w-4 h-4 border-2 border-bronze-dark border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">This usually takes 5-10 seconds. Please don&apos;t close this tab.</span>
              </div>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="payment-status-paid">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CheckCircle size={18} weight="fill" /> Booking confirmed
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
                Thank you. <em className="font-serif text-bronze-dark">Your appointment is confirmed.</em>
              </h1>
              <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
                Check your email{inviteeEmail ? <> at <b>{inviteeEmail}</b></> : ""} for a confirmation and calendar invite. The calendar invite will drop straight into your Google, Apple or Outlook calendar.
              </p>
            </div>
          )}

          {status === "manual" && (
            <div data-testid="payment-status-manual">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CheckCircle size={18} weight="fill" /> Payment received
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Thank you - we&apos;ll confirm your time shortly.</h1>
              <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
                Your payment has gone through. Our team is confirming your appointment personally and will email{inviteeEmail ? <> <b>{inviteeEmail}</b></> : " you"} a confirmation and calendar invite shortly. Please don&apos;t pay again.
              </p>
            </div>
          )}

          {status === "slot_lost" && (
            <div data-testid="payment-status-slot-lost">
              <div className="eyebrow flex items-center gap-2 text-red-700">
                <WarningCircle size={18} weight="fill" /> Time no longer available
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Sorry - that time was just taken.</h1>
              <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
                Someone else booked it while you were paying, so we couldn&apos;t confirm it. <b>You&apos;ve been refunded in full</b> - it usually shows on your card within 5-10 working days. We&apos;ve emailed you too.
              </p>
              <div className="mt-8"><Link to="/enquiry" data-testid="payment-slot-lost-rebook" className="btn-primary">Choose another time</Link></div>
            </div>
          )}

          {(status === "timeout" || status === "error") && (
            <div data-testid="payment-status-error">
              <div className="eyebrow flex items-center gap-2 text-red-700">
                <WarningCircle size={18} weight="fill" /> We&apos;re still confirming
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
                We couldn&apos;t confirm your payment yet.
              </h1>
              <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
                Stripe is taking longer than usual to confirm. Your card may still have been charged - please do not pay again. Contact us at{" "}
                <a href="mailto:enquiries@camcpc.co.uk" className="text-bronze-dark underline">enquiries@camcpc.co.uk</a>{" "}
                with your name and we&apos;ll sort your appointment personally.
              </p>
              {sessionId && (
                <p className="mt-3 text-xs text-ink-muted">Reference: {sessionId}</p>
              )}
              <div className="mt-8">
                <Link to="/contact" data-testid="payment-error-contact-cta" className="btn-primary">Contact the clinic</Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {status === "paid" && (
        <section className="container-ccc py-12 max-w-5xl">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="bg-white border border-bronze-light/40 rounded-md p-8" data-testid="confirmed-booking-summary">
                <div className="eyebrow">Appointment details</div>
                {!booking && (
                  <p className="mt-4 text-sm text-ink-muted">
                    Loading your booking details… If they don&apos;t appear shortly, please check your email - the confirmation is already on its way.
                  </p>
                )}
                {booking && (
                  <>
                    <h2 className="font-serif text-2xl mt-2">{booking.event_name || "Consultation"}</h2>
                    <dl className="mt-6 space-y-4 text-sm">
                      <div className="flex gap-3">
                        <dt className="w-24 shrink-0 text-ink-muted">When</dt>
                        <dd className="text-ink font-medium">{whenStr || "Details to follow by email"}</dd>
                      </div>
                      {booking.consultant && (
                        <div className="flex gap-3">
                          <dt className="w-24 shrink-0 text-ink-muted">Consultant</dt>
                          <dd className="text-ink">{booking.consultant}</dd>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <dt className="w-24 shrink-0 text-ink-muted">Where</dt>
                        <dd className="text-ink">{booking.location_label || "ACCI Building, Cambridge University Hospitals, Hills Road, Cambridge CB2 0QQ"}</dd>
                      </div>
                      {booking.invitee_email && (
                        <div className="flex gap-3">
                          <dt className="w-24 shrink-0 text-ink-muted">Email</dt>
                          <dd className="text-ink">{booking.invitee_email}</dd>
                        </div>
                      )}
                    </dl>
                  </>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/" className="btn-primary" data-testid="back-home-cta">Back to home</Link>
                  {booking && booking.reschedule_url && (
                    <a href={booking.reschedule_url} target="_blank" rel="noopener noreferrer"
                       className="btn-secondary" data-testid="reschedule-cta">
                      Reschedule
                    </a>
                  )}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-5 space-y-5">
              <div className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
                <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                  <Envelope size={16} weight="duotone" /> Check your inbox
                </div>
                <p className="mt-3 text-sm text-ink-muted">
                  We&apos;ve sent a confirmation email with a calendar invite (<b>appointment.ics</b>) and a short prep PDF with everything you need to bring.
                </p>
                <p className="mt-2 text-xs text-ink-muted">Can&apos;t see it? Please check your spam folder.</p>
              </div>
              <div className="p-6 bg-white border border-stone-200/60 rounded-md">
                <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                  <CalendarCheck size={16} weight="duotone" /> On the day
                </div>
                <ul className="mt-3 text-sm text-ink-muted space-y-2 list-disc list-inside">
                  <li>Arrive 10 minutes early.</li>
                  <li>Bring photo ID and a list of your current medications.</li>
                  <li>Bring any recent letters or test results if you have them.</li>
                </ul>
              </div>
              <GettingHere compact />
            </aside>
          </div>
        </section>
      )}
    </div>
  );
}
